import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../context/ToastContext';
import { ActionCenterPrefService } from '../../../services/actionCenterPref.service';

export interface ActionCenterPrefs {
    /** Card keys the person hid. Empty until loaded, and when nothing was ever saved. */
    hidden: ReadonlySet<string>;
    /** False while the saved preference is loading: the Action Center waits rather than flash cards that are then hidden. */
    ready: boolean;
    /** True while a save is in flight; the UI blocks a second save until it settles. */
    saving: boolean;
    /** Hide exactly these keys now, save on the server, and revert with a toast when the save fails. */
    save: (hiddenKeys: string[]) => void;
}

const NONE: ReadonlySet<string> = new Set<string>();
/** A slow read must never leave the Action Center spinning: after this long, show every card. */
export const ACTION_CENTER_PREF_READ_TIMEOUT_MS = 3000;

/**
 * The caller's own hidden Action Center cards for one institution (shared by every place the Action Center renders;
 * today only the home board). A failed or slow read means "nothing hidden", never a stuck spinner.
 */
export function useActionCenterPrefs(institutionId: string): ActionCenterPrefs {
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const [state, setState] = useState<{ institutionId: string; hidden: ReadonlySet<string> } | null>(null);
    const [saving, setSaving] = useState(false);
    // The last list the server confirmed (or the read returned): what a failed save reverts to.
    const confirmed = useRef<ReadonlySet<string>>(NONE);
    // Counts saves so only the latest one may revert.
    const seqRef = useRef(0);
    // Set when the person saves: a read still in flight must then never overwrite their pick.
    const userSaved = useRef(false);

    useEffect(() => {
        let live = true;
        userSaved.current = false;
        seqRef.current += 1;
        setSaving(false);
        setState(null);
        const settle = (hidden: ReadonlySet<string>) => {
            if (!live || userSaved.current) return;
            confirmed.current = hidden;
            setState({ institutionId, hidden });
        };
        const timer = setTimeout(() => settle(NONE), ACTION_CENTER_PREF_READ_TIMEOUT_MS);
        ActionCenterPrefService.get(institutionId)
            .then(r => settle(new Set(r?.hiddenKeys ?? [])))
            .catch(() => settle(NONE))
            .finally(() => clearTimeout(timer));
        return () => { live = false; clearTimeout(timer); };
    }, [institutionId]);

    const save = useCallback((hiddenKeys: string[]) => {
        const next = new Set(hiddenKeys);
        const seq = ++seqRef.current;
        userSaved.current = true;
        setSaving(true);
        setState({ institutionId, hidden: next });
        ActionCenterPrefService.save(institutionId, Array.from(next))
            .then(() => {
                if (seq !== seqRef.current) return;
                confirmed.current = next;
                setSaving(false);
            })
            .catch(err => {
                if (seq !== seqRef.current) return;
                setSaving(false);
                setState({ institutionId, hidden: confirmed.current });
                toastRef.current.showApiError?.(err, 'Could not save your card choice');
            });
    }, [institutionId]);

    const current = state && state.institutionId === institutionId ? state : null;
    return { hidden: current?.hidden ?? NONE, ready: current !== null, saving, save };
}
